import React, { useState, useEffect } from "react";
import { fetchApprovedInsights } from "../../services/mediaApi";
import { fetchAllInsights, deleteInsight as deleteInsightApi, isPendingStatus } from "../../services/insightApi";
import { getInsightImageUrl, logInsightImageError } from "../../utils/insightImage";
import "./Newsletter.css";
import "../ServiceDetail/ServiceDetail.css";

import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import TextType from "../Home/TextType";
import SplitText from "../SplitText/SplitText";
import { ArrowLeft, Trash2, ShieldCheck, LogOut, FileText, User, LayoutDashboard } from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";

export default function Newsletter() {
    const navigate = useNavigate();
    const { user, isAuthenticated, isAdmin, logout } = useAuth();
    const [insights, setInsights] = useState([]);
    const [loading, setLoading] = useState(true);
    const [pendingCount, setPendingCount] = useState(0);

    useEffect(() => {
        loadInsights();
        if (isAdmin) {
            checkPendingInsights();
            const interval = setInterval(checkPendingInsights, 10000);
            return () => clearInterval(interval);
        }
    }, [isAdmin]);

    const checkPendingInsights = async () => {
        try {
            const response = await fetchAllInsights();
            // Handle both array and object responses
            const data = Array.isArray(response.data) ? response.data : (response.data.insights || []);
            const pending = data.filter((i) => isPendingStatus(i.status));
            setPendingCount(pending.length);
        } catch (error) {
            console.error("Failed to check pending insights:", error);
        }
    };

    const loadInsights = async () => {
        setLoading(true);
        try {
            const { insights: data } = await fetchApprovedInsights();
            setInsights(data);
        } catch (error) {
            console.error("Failed to fetch insights:", error);
        } finally {
            setLoading(false);
        }
    };

    const handleCardClick = (item) => {
        navigate(`/newsletter/${item._id}`);
    };

    const handleDelete = async (e, id) => {
        e.stopPropagation(); // Prevent card click
        if (window.confirm("Are you sure you want to remove this insight?")) {
            try {
                await deleteInsightApi(id);
                // Update local state immediately
                setInsights(prev => prev.filter(i => i._id !== id));
                loadInsights(); // Refresh the list from server
            } catch (error) {
                console.error("Failed to delete insight:", error);
                alert("Failed to delete insight. Please try again.");
            }
        }
    };

    return (
        <div className="service-page-wrapper">
            <div className="admin-actions">
                {isAdmin && pendingCount > 0 && (
                    <motion.div
                        className="pending-badge-float"
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        onClick={() => navigate("/admin-dashboard")}
                    >
                        <FileText size={16} />
                        <span>{pendingCount} Pending</span>
                    </motion.div>
                )}
                {isAuthenticated && !isAdmin && (
                    <button className="admin-toggle-btn" onClick={() => navigate("/dashboard")} title="My Dashboard">
                        <LayoutDashboard size={20} />
                    </button>
                )}
                {!isAuthenticated && (
                    <button className="admin-toggle-btn" onClick={() => navigate("/admin-login")} title="Admin Login">
                        <ShieldCheck size={20} />
                    </button>
                )}
                {isAdmin && (
                    <>
                        <button className="admin-toggle-btn active" onClick={() => navigate("/admin-dashboard")} title="Admin Dashboard">
                            <ShieldCheck size={20} />
                        </button>
                        <button className="admin-toggle-btn" onClick={() => { logout(); navigate("/open"); }} title="Logout">
                            <LogOut size={20} />
                        </button>
                    </>
                )}
            </div>
            {/* ── HERO BANNER ── */}
            <section className="main-hero">
                <h4 className="main-hero-sub">TTS INSIGHTS</h4>
                <h1 className="main-hero-title">
                    <TextType
                        text={["Stay Updated with TTS Insights"]}
                        typingSpeed={75}
                        pauseDuration={2000}
                        deletingSpeed={50}
                        showCursor={true}
                        cursorCharacter="|"
                        loop={true}
                    />
                </h1>
            </section>

            <section className="service-inpage">
                <div className="container">
                    <div className="newsletter-top-header">
                        <div className="section-header-centered">
                            <SplitText
                                text="TTS Insights"
                                className="heading-unified"
                                tag="h2"
                                delay={50}
                                duration={0.8}
                                ease="power3.out"
                                splitType="chars"
                                threshold={0.1}
                            />
                            <div className="heading-underline"></div>
                        </div>
                        <button
                            className="back-home-btn small-btn"
                            onClick={() => navigate(isAuthenticated ? "/login" : "/open")}
                        >
                            {isAuthenticated ? "Add Content" : "Login / Add"}
                            <ArrowLeft size={18} style={{ transform: "rotate(180deg)" }} />
                        </button>
                    </div>

                    {/* Insights Grid (Showing only uploaded images) */}
                    <div className="newsletter-grid dynamic-only">
                        {loading ? (
                            <div className="no-insights-placeholder">
                                <p>Loading insights...</p>
                            </div>
                        ) : insights.length > 0 ? (
                            insights.map((item, index) => (
                                <motion.div 
                                    key={item._id}
                                    className="newsletter-card image-only clickable"
                                    onClick={() => handleCardClick(item)}
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.5, delay: index * 0.1 }}
                                >
                                    {isAdmin && (
                                        <div className="admin-card-controls">
                                            {item.status?.toLowerCase() === "pending" && (
                                                <div className="card-status-badge pending">PENDING</div>
                                            )}
                                            <button 
                                                className="delete-card-btn" 
                                                onClick={(e) => handleDelete(e, item._id)}
                                                title="Remove this picture"
                                            >
                                                <Trash2 size={16} />
                                            </button>
                                        </div>
                                    )}
                                    <div className="newsletter-card-image">
                                        <img
                                            src={getInsightImageUrl(item.image)}
                                            alt={item.title}
                                            width="400"
                                            height="250"
                                            loading="lazy"
                                            onError={(e) => {
                                                logInsightImageError(item, e);
                                            }}
                                        />
                                    </div>
                                    <div className="newsletter-card-footer">
                                        <div className="insight-card-main-info">
                                            <h3>{item.title}</h3>
                                            <div className="insight-author">
                                                <User size={14} />
                                                <span>{item.author?.name || "Anonymous"}</span>
                                            </div>
                                        </div>
                                    </div>
                                </motion.div>
                            ))
                        ) : (
                            <div className="no-insights-placeholder">
                                <p>No insights published yet. Click "Add" to submit your first insight for approval!</p>
                            </div>
                        )}
                    </div>

                    {isAdmin && user && (
                        <motion.div
                            className="admin-footer-details"
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6 }}
                        >
                            <div className="admin-details-card">
                                <div className="admin-details-header">
                                    <ShieldCheck size={24} color="#7c5dfa" />
                                    <h3>Administrator Session</h3>
                                </div>
                                <p>Signed in as {user.name} ({user.email})</p>
                                <button className="go-to-admin-btn" onClick={() => navigate("/admin-dashboard")}>
                                    Go to Admin Dashboard
                                </button>
                            </div>
                        </motion.div>
                    )}
                </div>

                <div className="back-home-dual-wrapper">
                    <button className="back-home-btn" onClick={() => navigate("/")}>
                        <ArrowLeft size={20} />
                        Back to Home Page
                    </button>
                </div>
            </section>
        </div>
    );
}
